import Lenis from 'lenis';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { gsap, ScrollTrigger } from '@/lib/gsap';

export interface ScrollContextValue {
  /** Page scroll progress 0..1. A ref, not state: read it inside ticker callbacks. */
  progressRef: MutableRefObject<number>;
  scrollToTop: () => void;
  /** Scrolls to a section by id. No-op while the section does not exist yet. */
  scrollToSection: (id: string) => void;
  /**
   * Holds the page still under an overlay (the phone menu). Lenis stops, and its stopped
   * class clips the document so native touch scrolling stops with it. `scrollToSection` is
   * ignored while locked, so unlock before scrolling from inside the overlay.
   */
  lockScroll: (locked: boolean) => void;
}

export const ScrollContext = createContext<ScrollContextValue | null>(null);

interface ScrollProviderProps {
  children: ReactNode;
  /** Scrolling stays locked until fonts are ready. */
  enabled: boolean;
}

export function ScrollProvider({ children, enabled }: ScrollProviderProps) {
  const progressRef = useRef(0);
  const lenisRef = useRef<Lenis | null>(null);
  const enabledRef = useRef(enabled);
  const lockedRef = useRef(false);
  /** Fonts became ready while locked: the measuring refresh waits for the unlock. */
  const refreshOnUnlockRef = useRef(false);

  // Mount-only: Lenis is created once. Reduced motion is handled by Lenis itself
  // (respectReducedMotion defaults to true), so no dependency should ever re-run this.
  useEffect(() => {
    history.scrollRestoration = 'manual';

    const lenis = new Lenis({
      lerp: 0.085,
      wheelMultiplier: 0.95,
      touchMultiplier: 1.4,
      autoRaf: false,
    });
    lenisRef.current = lenis;
    lenis.scrollTo(0, { immediate: true });
    if (!enabledRef.current) lenis.stop();

    lenis.on('scroll', (instance) => {
      progressRef.current = instance.progress;
      ScrollTrigger.update();
    });

    const tick = (time: number) => lenis.raf(time * 1000);
    // prioritize=true: advance scroll before any frame-render callbacks in the same tick.
    gsap.ticker.add(tick, false, true);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) return;
    if (lockedRef.current) {
      refreshOnUnlockRef.current = true;
      return;
    }
    lenisRef.current?.start();
    ScrollTrigger.refresh();
  }, [enabled]);

  const scrollToTop = useCallback(() => {
    lenisRef.current?.scrollTo(0, { duration: 1.4 });
  }, []);

  const scrollToSection = useCallback((id: string) => {
    // A pinned section's trigger start is exact where its bounding rect is not; the plain
    // element covers normal-flow sections. Sections not built yet are skipped quietly.
    const trigger = ScrollTrigger.getById(id);
    const target = trigger ? trigger.start + 1 : document.getElementById(id);
    if (target === null) return;
    lenisRef.current?.scrollTo(target, { duration: 1.4 });
  }, []);

  const lockScroll = useCallback((locked: boolean) => {
    lockedRef.current = locked;
    const lenis = lenisRef.current;
    if (!lenis) return;
    // Unlocking never starts a page still waiting on fonts; the enabled effect does that.
    if (locked) {
      lenis.stop();
    } else if (enabledRef.current) {
      lenis.start();
      if (refreshOnUnlockRef.current) {
        refreshOnUnlockRef.current = false;
        ScrollTrigger.refresh();
      }
    }
  }, []);

  const value = useMemo<ScrollContextValue>(
    () => ({ progressRef, scrollToTop, scrollToSection, lockScroll }),
    [scrollToTop, scrollToSection, lockScroll],
  );

  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
}
